import { useState, useEffect } from 'react';
import { Mail, Save } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface EmailPopupConfig {
  enabled: boolean;
  headline: string;
  body: string;
  button_text: string;
  success_message: string;
  delay_seconds: number;
  show_incentive: boolean;
  incentive_code: string;
  incentive_description: string;
}

const DEFAULTS: EmailPopupConfig = {
  enabled: false,
  headline: 'Get 10% Off Your First Order',
  body: 'Join our newsletter for exclusive deals, exclusive deals and early access to new products.',
  button_text: 'Subscribe & Save',
  success_message: 'Welcome! Check your inbox for your discount code.',
  delay_seconds: 5,
  show_incentive: true,
  incentive_code: 'WELCOME10',
  incentive_description: '10% off your first order',
};

export default function EmailPopup() {
  const [config, setConfig] = useState<EmailPopupConfig>(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ settings: Record<string, any> }>('/admin/settings');
      const saved = response.settings.email_popup;
      if (saved) {
        setConfig({ ...DEFAULTS, ...saved });
      }
    } catch (err) {
      setError('Failed to load settings. Please try again.');
      console.error('Error fetching email popup config:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    try {
      setIsSaving(true);
      setError(null);
      await api.put('/admin/settings', {
        settings: { email_popup: config },
      });
      setSuccessMessage('Email popup settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Error saving email popup config:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function handleChange<K extends keyof EmailPopupConfig>(field: K, value: EmailPopupConfig[K]) {
    setConfig(prev => ({ ...prev, [field]: value }));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading email popup settings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Email Signup Popup</h1>
        </div>
        <p className="text-gray-600">Configure the email signup popup shown to new visitors</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      <form onSubmit={handleSave} noValidate>
        <div className="space-y-6">
          <Card title="Enable Popup" padding="lg">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="popup_enabled"
                checked={config.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="popup_enabled" className="text-sm font-medium text-gray-700">
                Show email signup popup to visitors
              </label>
            </div>
          </Card>

          <Card title="Popup Content" padding="lg">
            <div className="space-y-6">
              <Input
                label="Headline"
                value={config.headline}
                onChange={(e) => handleChange('headline', e.target.value)}
                placeholder="Get 10% Off Your First Order"
              />

              <Textarea
                label="Body Text"
                value={config.body}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder="Join our newsletter for exclusive deals..."
                rows={3}
              />

              <Input
                label="Button Text"
                value={config.button_text}
                onChange={(e) => handleChange('button_text', e.target.value)}
                placeholder="Subscribe & Save"
              />

              <Textarea
                label="Success Message"
                value={config.success_message}
                onChange={(e) => handleChange('success_message', e.target.value)}
                placeholder="Welcome! Check your inbox for your discount code."
                rows={2}
                helperText="Shown after a visitor successfully subscribes"
              />
            </div>
          </Card>

          <Card title="Timing" padding="lg">
            <Input
              label="Delay (seconds)"
              type="number"
              value={String(config.delay_seconds)}
              onChange={(e) => handleChange('delay_seconds', Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="5"
              min="0"
              step="1"
              helperText="How long to wait before showing the popup (0 = immediately)"
            />
          </Card>

          <Card title="Incentive" padding="lg">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="show_incentive"
                  checked={config.show_incentive}
                  onChange={(e) => handleChange('show_incentive', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="show_incentive" className="text-sm font-medium text-gray-700">
                  Show discount incentive after signup
                </label>
              </div>

              {config.show_incentive && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Discount Code"
                    value={config.incentive_code}
                    onChange={(e) => handleChange('incentive_code', e.target.value)}
                    placeholder="WELCOME10"
                  />

                  <Input
                    label="Incentive Description"
                    value={config.incentive_description}
                    onChange={(e) => handleChange('incentive_description', e.target.value)}
                    placeholder="10% off your first order"
                  />
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isSaving}
          >
            <Save className="w-5 h-5 mr-2" aria-hidden="true" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
}
